import { ReactElement, useState, useCallback, useEffect, useMemo } from 'react';
import {
  DialogTitle,
  DialogContentText,
  Button,
  Link,
  Typography,
  Stack,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik, FormikProvider, FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';

import { useTypePartnerCompany } from '~hooks/useTypePartnerCompany';
import { useSampleTemplates } from '~hooks/useSampleTemplates';
import { useImporterCustomersProgressListener } from '~hooks/useImporterCustomersProgressListener';
import { useLazyRequest } from '~hooks/useLazyRequest';
import { importCustomersFromCSV } from '~services/customer';
import { CustomerImporter } from '~services/customer/types';
import {
  getImporterCustomerFromCSVAlertDescription,
  hasGeoImportErrors,
  getImporterCustomerFromCSVErrorAlert,
} from '~utils/customer';
import { toAbsoluteUrl } from '~utils/assetsHelpers';
import FileUploadV2 from '~components/FileUploadV2';
import ImporterProgress from '~components/ImporterProgress';

import { useDialogImportCustomersFromCSVContext } from '../Context';
import DialogImportCustomersFromCSVImportErrorsDetail from '../ImportErrorsDetail';
import {
  DialogImportCustomersFromCSVContentContainer,
  DialogImportCustomersFromCSVActions,
} from '../styles';
import { FIELDS_NAME, initialValues, validationSchema } from './utils';

const DialogImportCustomersFromCSVContent = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { isCarrier } = useTypePartnerCompany();
  const { customer: dowloadTemplateUrl } = useSampleTemplates();

  const { onClose, onSubmitSuccess, onUpdateErrors } =
    useDialogImportCustomersFromCSVContext();

  const [runImporterProgressListener, setImporterProgressListener] =
    useState(false);
  const [isCompletedRequest, setIsCompletedRequest] = useState(false);
  const [importedCustomersData, setImportedCustomersData] =
    useState<CustomerImporter>({
      errorRows: 0,
      importedRows: 0,
      message: '',
      totalRows: 0,
      errors: [],
      omittedErrors: 0,
    });

  const {
    status,
    updateImporterStatus,
    data: { progress, step, total },
  } = useImporterCustomersProgressListener({
    runListenerUpdates: runImporterProgressListener,
    onCompleted: () => {
      setImporterProgressListener(false);
    },
  });

  const loading = useMemo(() => status === 'inProgress', [status]);

  const isImporting = useMemo(
    () => loading || status === 'completed' || runImporterProgressListener,
    [loading, status, runImporterProgressListener],
  );

  const isCompletedProcessImporter = useMemo(
    () => status === 'completed' && isCompletedRequest,
    [status, isCompletedRequest],
  );

  const importerProgressTitle = useMemo(
    () =>
      isCompletedProcessImporter
        ? 'Importación finalizada'
        : 'Analizando archivo',
    [isCompletedProcessImporter],
  );

  const importerProgressDescription = useMemo(
    () =>
      isCompletedProcessImporter
        ? getImporterCustomerFromCSVAlertDescription(importedCustomersData)
        : `Analizando fila ${step} de ${total}`,
    [isCompletedProcessImporter, importedCustomersData, step, total],
  );

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null;

    if (isCompletedProcessImporter && importedCustomersData.errorRows < 1) {
      timeout = setTimeout(() => {
        onClose();
      }, 1500);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [importedCustomersData.errorRows, isCompletedProcessImporter, onClose]);

  const [, , , executeImportCustomers] = useLazyRequest({
    request: importCustomersFromCSV,
    withPostSuccess: (response) => {
      const responseImporter = response.data?.data as CustomerImporter;

      setImportedCustomersData(responseImporter);

      setIsCompletedRequest(true);
      onSubmitSuccess();
    },
    withPostFailure: () => {
      setIsCompletedRequest(true);
      enqueueSnackbar('Ha ocurrido un error, intente nuevamente', {
        variant: 'error',
      });
    },
  });

  const onSubmit = useCallback(
    async (
      values: typeof initialValues,
      { setSubmitting }: FormikHelpers<typeof initialValues>,
    ) => {
      setImporterProgressListener(true);
      updateImporterStatus('inProgress');
      setIsCompletedRequest(false);

      await executeImportCustomers(values[FIELDS_NAME.FILES][0]);

      setSubmitting(false);
    },
    [updateImporterStatus, executeImportCustomers],
  );

  const formikBag = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const { submitForm, isSubmitting, dirty, isValid, values } = formikBag;

  const disabledSubmit = useMemo(
    () => !dirty || !isValid || !values[FIELDS_NAME.FILES],
    [dirty, isValid, values],
  );

  const [showErrorsDetail, setShowErrorsDetail] = useState(false);

  const hasImportErrors = useMemo(
    () => isCompletedProcessImporter && importedCustomersData.errorRows >= 1,
    [isCompletedProcessImporter, importedCustomersData.errorRows],
  );

  const hasGeoErrors = useMemo(
    () => hasGeoImportErrors(importedCustomersData.errors),
    [importedCustomersData.errors],
  );

  const toggleErrorsDetail = useCallback(
    () => setShowErrorsDetail((prev) => !prev),
    [],
  );

  return (
    <FormikProvider value={formikBag}>
      <DialogTitle>Importar Contactos Frecuentes</DialogTitle>

      <DialogImportCustomersFromCSVContentContainer dividers>
        {!isImporting && (
          <DialogContentText color="text.primary">
            Importar contactos frecuentes desde un archivo de forma masiva
          </DialogContentText>
        )}

        <FileUploadV2
          name={FIELDS_NAME.FILES}
          labelImg={{
            src: toAbsoluteUrl('/images/import_file.png'),
            alt: 'Import File',
          }}
          accept=".csv, .xls, .xlsx"
          disabled={isImporting}
        />

        {!isImporting && (
          <DialogContentText variant="caption">
            Si deseas importar de forma masiva utilizando un archivo, puedes{' '}
            <Link
              href={dowloadTemplateUrl}
              download
              color="primary"
              underline="hover"
              fontWeight="bold"
            >
              descargar y modificar la plantilla de ejemplo.
            </Link>{' '}
            Recuerda respetar el orden de las columnas.{' '}
            <Typography
              variant="inherit"
              component="span"
              // @NEW-GIVER-DASHBOARD: Hide until the help URL is available for Givers
              visibility={isCarrier ? 'visible' : 'hidden'}
            >
              Si necesitasinformación consulta la{' '}
              <Link
                href="https://routix.tawk.help/article/cargar-clientes-de-forma-masiva"
                download
                color="primary"
                underline="hover"
                fontWeight="bold"
              >
                ayuda.
              </Link>
            </Typography>
          </DialogContentText>
        )}

        {isImporting && (
          <ImporterProgress
            title={importerProgressTitle}
            description={importerProgressDescription}
            progress={progress}
            warning={importedCustomersData.errorRows >= 1}
          />
        )}

        {hasImportErrors && (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography variant="body2" fontWeight={600}>
                {getImporterCustomerFromCSVErrorAlert(
                  importedCustomersData.errorRows,
                )}
              </Typography>

              <Button size="small" onClick={toggleErrorsDetail}>
                {showErrorsDetail ? 'Ocultar detalle' : 'Ver detalle'}
              </Button>
            </Stack>

            {showErrorsDetail && (
              <DialogImportCustomersFromCSVImportErrorsDetail
                errors={importedCustomersData.errors}
                omittedErrors={importedCustomersData.omittedErrors}
              />
            )}
          </>
        )}
      </DialogImportCustomersFromCSVContentContainer>

      <DialogImportCustomersFromCSVActions>
        <Button color="secondary" onClick={onClose}>
          Cerrar
        </Button>

        {!isImporting && (
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={submitForm}
            loading={loading || isSubmitting}
            disabled={disabledSubmit}
          >
            Importar
          </LoadingButton>
        )}

        {hasImportErrors && hasGeoErrors && (
          <Button variant="contained" color="primary" onClick={onUpdateErrors}>
            Corregir problemas de geolocalización
          </Button>
        )}
      </DialogImportCustomersFromCSVActions>
    </FormikProvider>
  );
};

export default DialogImportCustomersFromCSVContent;
